import { useState, useEffect } from "react";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { anatomyConstants } from "../../constants/anatomyConstants";

const SideButtonsControls = ({
  isAnnotationMode,
  isDistanceMeasuringMode,
  isScalpelMode,
  isExtractMode,
  isDrillMode,
  isMobile,
  isFullscreen,
  isFullScreen,
  toggleAnnotationMode,
  toggleDistanceMeasuringMode,
  toggleScalpelMode,
  toggleDrillMode,
  toggleCameraOrientationMode,
  toggleSurgeryPosMode,
  toggleSectionViewMode,
  toggleFullScreen,
  toggleVideoRecording,
  isRecording,
  dicomBounds,
  toggleSectionViewModeSimple,
  isPointLinkingMode,
  togglePointLinkingMode,
  toggleExtractMode,
  isSurgeryPosMode,
  isSectionViewMode,
  isCameraOrientationMode,
  toggleCenterlineMode,
  isCenterlineMode,
  isStentMode,
  toggleStentMode,
}) => {
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== "undefined"
      ? window.innerWidth > window.innerHeight
      : false
  );

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  const isFull = isFullScreen;

  // Mobile portrait (upside down): horizontal scroll at top
  // Mobile landscape (side-view): vertical scroll on left
  // Desktop fullscreen: vertical layout
  // Desktop not fullscreen: 2-column grid
  const isMobileLandscape = isMobile && isLandscape && isFull;
  const isMobilePortrait = isMobile && !isLandscape && isFull;

  const containerClass = isMobilePortrait
    ? "flex flex-row items-center space-x-2 overflow-x-auto scrollbar-hide"
    : isMobileLandscape
      ? "flex flex-col items-start space-y-2 overflow-y-auto scrollbar-hide max-h-[60vh]"
      : isFull
        ? "flex flex-col items-start space-y-2"
        : "grid grid-cols-2 gap-2 items-start";

  const buttonClass =
    isFull || isMobileLandscape || isMobilePortrait
      ? "w-12 h-12 flex justify-center items-center rounded hover:bg-gray-700 flex-shrink-0"
      : "py-1 px-2 rounded whitespace-nowrap hover:bg-gray-700";

  const iconClass =
    isFull || isMobileLandscape || isMobilePortrait ? "h-8 w-8" : "h-6 w-6";

  const icon = (filename) => `${anatomyConstants.imagePath}/${filename}`;
  // CSS filter to tint dark PNG icons to the brand green #30A497
  const iconStyle = { filter: "invert(55%) sepia(60%) saturate(400%) hue-rotate(135deg) brightness(95%)" };

  return (
    <div className={containerClass}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleStentMode();
        }}
        className={`relative group ${buttonClass} ${isStentMode ? "bg-gray-700" : ""
          }`}
      >
        <img src={icon("stent.svg")} alt="Stent" className={`${iconClass} scale-125`} style={iconStyle} />
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          Stent
        </span>
      </button>
      {dicomBounds && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            togglePointLinkingMode();
          }}
          className={`relative group ${buttonClass} ${isPointLinkingMode ? "bg-gray-700" : ""
            }`}
        >
          <img
            src={icon("point_linking.png")}
            alt="Point Linking"
            className={iconClass}
            style={iconStyle}
          />
          <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
            Point Linking
          </span>
        </button>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleAnnotationMode();
        }}
        className={`relative group ${buttonClass} ${isAnnotationMode ? "bg-gray-700" : ""
          }`}
      >
        <img src={icon("annotate.png")} alt="Annotate" className={iconClass} style={iconStyle} />
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          Annotate
        </span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleDistanceMeasuringMode();
        }}
        className={`relative group ${buttonClass} ${isDistanceMeasuringMode ? "bg-gray-700" : ""
          }`}
      >
        <img src={icon("measure.png")} alt="Measure" className={iconClass} style={iconStyle} />
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          Measure
        </span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleScalpelMode();
        }}
        className={`relative group ${buttonClass} ${isScalpelMode ? "bg-gray-700" : ""
          }`}
      >
        <img src={icon("scalpel.png")} alt="Scalpel" className={iconClass} style={iconStyle} />
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          Scalpel
        </span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleExtractMode();
        }}
        className={`relative group ${buttonClass} ${isExtractMode ? "bg-gray-700" : ""
          }`}
      >
        <img src={icon("extract.png")} alt="Extract" className={iconClass} style={iconStyle} />
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          Extract
        </span>
      </button>
      {/* <button
        onClick={(e) => {
          e.stopPropagation();
          toggleDrillMode();
        }}
        className={`relative group ${buttonClass} ${
          isDrillMode ? "bg-gray-700" : ""
        }`}
      >
        <img src={icon("drill.png")} alt="Drill" className={iconClass} />
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          Drill
        </span>
      </button> */}

      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleCameraOrientationMode();
        }}
        className={`relative group ${buttonClass} ${isCameraOrientationMode ? "bg-gray-700" : ""
          }`}
      >
        <img
          src={icon("camera_orientation.png")}
          alt="Camera Orientation"
          className={iconClass}
          style={iconStyle}
        />
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          Camera Orientation
        </span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleSurgeryPosMode();
        }}
        className={`relative group ${buttonClass} ${isSurgeryPosMode ? "bg-gray-700" : ""
          }`}
      >
        <img
          src={icon("surgery_position.png")}
          alt="Surgery Position"
          className={iconClass}
          style={iconStyle}
        />
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          Surgery Position
        </span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleCenterlineMode();
        }}
        className={`relative group ${buttonClass} ${isCenterlineMode ? "bg-gray-700" : ""
          }`}
      >
        <img
          src={icon("center_line.png")}
          alt="Centerline"
          className={iconClass}
          style={iconStyle}
        />
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          Centerline
        </span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (dicomBounds === null) {
            toggleSectionViewModeSimple();
          } else {
            toggleSectionViewMode();
          }
        }}
        className={`relative group ${buttonClass} ${isSectionViewMode ? "bg-gray-700" : ""
          }`}
      >
        <img
          src={icon("section_view.png")}
          alt="Section View"
          className={iconClass}
          style={iconStyle}
        />
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          Section View
        </span>
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleVideoRecording();
        }}
        className={`relative group ${buttonClass} ${isRecording ? "bg-[var(--main-color)] text-white" : ""
          }`}
      >
        {isRecording ? (
          <PauseCircleIcon className={iconClass} sx={{ color: "#30A497" }} />
        ) : (
          <img
            src={icon("screenshot.png")}
            alt="Record Video"
            className={iconClass}
            style={iconStyle}
          />
        )}
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
          {isRecording ? "Stop Recording" : "Record Video"}
        </span>
      </button>
      {!isMobile && isFull && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleFullScreen();
          }}
          className={`relative group ${buttonClass}`}
        >
          {isFullscreen ? (
            <FullscreenExitIcon className={iconClass} sx={{ color: "#30A497" }} />
          ) : (
            <FullscreenIcon className={iconClass} sx={{ color: "#30A497" }} />
          )}
          <span className="absolute left-full top-1/2 -translate-y-1/2 ml-2 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none whitespace-nowrap z-20 transition-all duration-200">
            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          </span>
        </button>
      )}
    </div>
  );
};

export default SideButtonsControls;
